import React from "react";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  TextField,
  Typography,
  Grid,
  IconButton,
  Paper,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useStyles } from "./styles";   

type TimeRange = {
  start: string;
  end: string;
};

type WorkingDay = {
  day: string;
  openingHour: TimeRange[];
};

type UnworkingDay = {
  start: string;
  end: string;
};

type Props = {
  label: string;
  value: WorkingDay[] | UnworkingDay[];
  onChange: (value: WorkingDay[] | UnworkingDay[]) => void;
  error?: string;
  marginBottom?: string;
  disabled?: boolean;
  isUnworkingDays?: boolean;
};

const DAYS_OF_WEEK = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

// Type guards
const isWorkingDayArray = (arr: any[]): arr is WorkingDay[] => {
  return arr.length === 0 || (arr[0] && "day" in arr[0]);
};

const isUnworkingDayArray = (arr: any[]): arr is UnworkingDay[] => {
  return arr.length === 0 || (arr[0] && "start" in arr[0] && "end" in arr[0] && !("day" in arr[0]));
};

export const CustomWorkingDaysInput: React.FC<Props> = (props) => {
  const {
    label,
    value,
    onChange,
    error,
    disabled = false,
    isUnworkingDays = false,
  } = props;

  const classes = useStyles(props);

  const formatTime = (time: string) => {
    if (!time) return "";
    return time;
  };

  const handleAddDay = () => {
    if (isUnworkingDays && isUnworkingDayArray(value)) {
      const newUnworkingDay: UnworkingDay = {
        start: new Date().toISOString().split("T")[0],
        end: new Date().toISOString().split("T")[0],
      };
      onChange([...value, newUnworkingDay]);
    } else if (!isUnworkingDays && isWorkingDayArray(value)) {
      const existingDays = value.map((day) => day.day);
      const availableDays = DAYS_OF_WEEK.filter((d) => !existingDays.includes(d));

      if (availableDays.length) {
        const newWorkingDay: WorkingDay = {
          day: availableDays[0],
          openingHour: [{ start: "09:00", end: "17:00" }],
        };
        onChange([...value, newWorkingDay]);
      }
    }
  };

const handleRemoveDay = (index: number) => {
  if (isWorkingDayArray(value)) {
    const newValue: WorkingDay[] = [...value];
    newValue.splice(index, 1);
    onChange(newValue);
  } else if (isUnworkingDayArray(value)) {
    const newValue: UnworkingDay[] = [...value];
    newValue.splice(index, 1);
    onChange(newValue);
  }
};

  const handleAddTimeRange = (dayIndex: number) => {
    if (!isUnworkingDays && isWorkingDayArray(value)) {
      const newWorkingDays = [...value];
      newWorkingDays[dayIndex].openingHour.push({ start: "09:00", end: "17:00" });
      onChange(newWorkingDays);
    }
  };

  const handleRemoveTimeRange = (dayIndex: number, timeIndex: number) => {
    if (!isUnworkingDays && isWorkingDayArray(value)) {
      const newWorkingDays = [...value];
      newWorkingDays[dayIndex].openingHour.splice(timeIndex, 1);
      onChange(newWorkingDays);
    }
  };

  const handleWorkingDayChange = (dayIndex: number, field: string, newValue: string) => {
    if (isWorkingDayArray(value)) {
      const newWorkingDays = [...value];
      if (field === "day") {
        newWorkingDays[dayIndex].day = newValue;
      }
      onChange(newWorkingDays);
    }
  };

  const handleTimeRangeChange = (
    dayIndex: number,
    timeIndex: number,
    field: "start" | "end",
    newValue: string
  ) => {
    if (isWorkingDayArray(value)) {
      const newWorkingDays = [...value];
      newWorkingDays[dayIndex].openingHour[timeIndex][field] = newValue;
      onChange(newWorkingDays);
    }
  };

  const handleUnworkingDayChange = (
    index: number,
    field: "start" | "end",
    newValue: string
  ) => {
    if (isUnworkingDayArray(value)) {
      const newUnworkingDays = [...value];
      newUnworkingDays[index][field] = newValue;
      onChange(newUnworkingDays);
    }
  };

  return (
    <FormControl className={classes.formControl} error={!!error} disabled={disabled}>
      <Box mb={2}>
        <Typography variant="subtitle1">{label}</Typography>
      </Box>

      <LocalizationProvider dateAdapter={AdapterDateFns}>
        {isUnworkingDays ? (
          <>
            {(value as UnworkingDay[]).map((period, index) => (
              <Paper key={index} className={classes.dayContainer} elevation={2}>
                <Box className={classes.dayHeader}>
                  <Typography variant="subtitle2">Closed Period {index + 1}</Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveDay(index)}
                    disabled={disabled}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      label="Start Date"
                      type="date"
                      value={period.start}
                      onChange={(e) => handleUnworkingDayChange(index, "start", e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      disabled={disabled}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="End Date"
                      type="date"
                      value={period.end}
                      onChange={(e) => handleUnworkingDayChange(index, "end", e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      disabled={disabled}
                    />
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </>
        ) : (
          <>
            {(value as WorkingDay[]).map((day, dayIndex) => (
              <Paper key={dayIndex} className={classes.dayContainer} elevation={2}>
                <Box className={classes.dayHeader}>
                  <TextField
                    select
                    SelectProps={{ native: true }}
                    label="Day"
                    value={day.day}
                    onChange={(e) =>
                      handleWorkingDayChange(dayIndex, "day", e.target.value)
                    }
                    disabled={disabled}
                  >
                    {DAYS_OF_WEEK.map((dayName) => (
                      <option key={dayName} value={dayName}>
                        {dayName}
                      </option>
                    ))}
                  </TextField>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveDay(dayIndex)}
                    disabled={disabled}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>

                {day.openingHour.map((range, timeIndex) => (
                  <Box key={timeIndex} className={classes.timeRangeContainer}>
                    <TextField
                      label="Start Time"
                      type="time"
                      value={formatTime(range.start)}
                      onChange={(e) =>
                        handleTimeRangeChange(dayIndex, timeIndex, "start", e.target.value)
                      }
                      className={classes.timeField}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 300 }}
                      disabled={disabled}
                    />
                    <Typography className={classes.timeDivider}>to</Typography>
                    <TextField
                      label="End Time"
                      type="time"
                      value={formatTime(range.end)}
                      onChange={(e) =>
                        handleTimeRangeChange(dayIndex, timeIndex, "end", e.target.value)
                      }
                      className={classes.timeField}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 300 }}
                      disabled={disabled}
                    />
                    {day.openingHour.length > 1 && (
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveTimeRange(dayIndex, timeIndex)}
                        disabled={disabled}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                ))}

                <Button
                  startIcon={<AddIcon />}
                  onClick={() => handleAddTimeRange(dayIndex)}
                  className={classes.addButton}
                  disabled={disabled}
                >
                  Add Time Range
                </Button>
              </Paper>
            ))}
          </>
        )}

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddDay}
          disabled={disabled || (!isUnworkingDays && isWorkingDayArray(value) && value.length >= 7)}
        >
          {isUnworkingDays ? "Add Closed Period" : "Add Working Day"}
        </Button>
      </LocalizationProvider>

      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
};
